import { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, Marker, TileLayer, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import findTransparencyPortalMap from "../../resources/Api/transparencyPortal/findTransparencyPortalMap";
import { TransparencyPortalMapType } from "../../types/types";
import { formatString } from "../../functions/functions";

export const TRANSPARENCY_PORTAL_PATH = "/portal-transparencia/";

// a API mantém cache de 30s, então atualizar mais rápido não traz posição nova
const REFRESH_INTERVAL_MS = 30000;

// mesmos ícones por tipo do mapa de rastreamento; tipo sem ícone cai no genérico
const iconCache: { [type: string]: L.Icon } = {};
const vehicleIcon = (type: string) => {
    const key = formatString(type || "generico");
    if (!iconCache[key]) {
        let iconUrl: string;
        try {
            iconUrl = require(`../../assets/files/cars/png/${key}.png`);
        } catch {
            iconUrl = require("../../assets/files/cars/png/generico.png");
        }
        iconCache[key] = L.icon({ iconUrl, iconSize: [35, 35], iconAnchor: [17, 35] });
    }
    return iconCache[key];
};

/**
 * Ajusta o mapa aos veículos apenas no primeiro carregamento, para não brigar com o zoom do usuário.
 */
const FitVehicles = ({ vehicles }: { vehicles: TransparencyPortalMapType["vehicles"] }) => {
    const map = useMap();
    const fitted = useRef(false);

    useEffect(() => {
        if (fitted.current || vehicles.length === 0) return;
        fitted.current = true;
        map.fitBounds(L.latLngBounds(vehicles.map((v) => [v.lat, v.lng])), { padding: [40, 40], maxZoom: 15 });
    }, [map, vehicles]);

    return null;
};

/**
 * TransparencyPortalPage - Página pública (sem login, sem menus) com placa e localização dos veículos do cliente.
 * @component
 */
const TransparencyPortalPage = () => {
    const linkToken = decodeURIComponent(window.location.pathname.slice(TRANSPARENCY_PORTAL_PATH.length).split("/")[0] ?? "");

    const [data, setData] = useState<TransparencyPortalMapType | null>(null);
    const [invalid, setInvalid] = useState<boolean>(false);
    const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

    const load = useCallback(async () => {
        try {
            const { data } = await findTransparencyPortalMap(linkToken);
            setData(data);
            setInvalid(false);
            setUpdatedAt(new Date());
        } catch (error: any) {
            // em falha de rede mantém a última posição; link inválido/desativado encerra a página
            // (a API responde erros como 409 com o status real no corpo)
            if (error?.response?.data?.status === "NOT_FOUND" || error?.response?.status === 404) {
                setInvalid(true);
            }
        }
    }, [linkToken]);

    useEffect(() => {
        document.title = "Portal da Transparência - Frota";
        load();
        const interval = setInterval(load, REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [load]);

    if (invalid) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100dvh", padding: 16, textAlign: "center", fontFamily: "sans-serif", color: "#334155" }}>
                Este link é inválido ou foi desativado.
            </div>
        );
    }

    if (!data) {
        return (
            <div className="tw-flex tw-min-h-[100dvh] tw-w-full tw-items-center tw-justify-center tw-bg-slate-100">
                <div className="tracker-loading-main">
                    <div className="ld-tracker ld-tracker-main">
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ position: "relative", height: "100dvh", width: "100vw" }}>
            <div
                style={{
                    position: "absolute",
                    zIndex: 1000,
                    top: 10,
                    left: "50%",
                    transform: "translateX(-50%)",
                    maxWidth: "calc(100vw - 32px)",
                    padding: "6px 14px",
                    borderRadius: 8,
                    background: "rgba(9, 65, 74, 0.92)",
                    color: "#fff",
                    fontFamily: "sans-serif",
                    fontSize: 13,
                    textAlign: "center",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                }}
            >
                <strong>{data.customerName}</strong>
                {" · "}
                {data.vehicles.length} veículo(s)
                {updatedAt && ` · atualizado às ${updatedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`}
            </div>

            <MapContainer
                center={{ lat: data.centerLat, lng: data.centerLng }}
                zoom={13}
                zoomControl={true}
                attributionControl
                style={{ height: "100%", width: "100%" }}
            >
                <TileLayer
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitVehicles vehicles={data.vehicles} />
                {data.vehicles.map((vehicle) => (
                    <Marker key={vehicle.plate} position={[vehicle.lat, vehicle.lng]} icon={vehicleIcon(vehicle.vehicleTypeName)}>
                        <Tooltip direction="top" offset={[0, -35]} permanent>
                            {vehicle.plate}
                        </Tooltip>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
};

export default TransparencyPortalPage;
